import { useEffect, useMemo } from "react";
import {
  BackSide,
  BufferGeometry,
  DoubleSide,
  Line as ThreeLine,
  LineBasicMaterial,
  Vector3 as ThreeVector3,
} from "three";
import { LabelSprite } from "../../scene/LabelSprite";
import { Sun } from "../../scene/Sun";
import { SimpleOrbitRig, type OrbitFacing } from "../shared/SimpleOrbitRig";
import {
  DAY_HOURS,
  localSunComponents,
  type HorizonSnapshot,
} from "./geometry";

function LinePath({
  color,
  points,
}: {
  readonly color: string;
  readonly points: readonly ThreeVector3[];
}) {
  const geometry = useMemo(
    () => new BufferGeometry().setFromPoints([...points]),
    [points],
  );
  const line = useMemo(
    () =>
      new ThreeLine(
        geometry,
        new LineBasicMaterial({ color, transparent: true, opacity: 0.9 }),
      ),
    [color, geometry],
  );

  useEffect(
    () => () => {
      geometry.dispose();
      line.material.dispose();
    },
    [geometry, line.material],
  );

  return <primitive object={line} />;
}

// Fixed pivot just above the observer keeps the horizon in frame while the
// pupil drags to follow the Sun.
const OBSERVER_TARGET = [0, 2.2, 0] as const;

function skyColor(altitudeDegrees: number): string {
  if (altitudeDegrees > 18) {
    return "#6fb3ff";
  }
  if (altitudeDegrees > 6) {
    return "#7ea6d6";
  }
  if (altitudeDegrees > 0) {
    return "#c47a4a";
  }
  if (altitudeDegrees > -6) {
    return "#3a2a3f";
  }
  return "#071018";
}

export function HorizonScene({
  classroomMode = false,
  snapshot,
  viewCue = 0,
}: {
  readonly classroomMode?: boolean;
  readonly snapshot: HorizonSnapshot;
  readonly viewCue?: number;
}) {
  const sunPosition = useMemo(
    () =>
      [
        snapshot.localSun[0] * 8.4,
        snapshot.localSun[1] * 8.4,
        snapshot.localSun[2] * 8.4,
      ] as [number, number, number],
    [snapshot.localSun],
  );
  const pathPoints = useMemo(() => {
    return Array.from({ length: 49 }, (_, index) => {
      const hour = (index / 48) * DAY_HOURS;
      const local = localSunComponents(
        snapshot.site.latitudeDegrees,
        snapshot.solarDeclinationDegrees,
        hour,
      );
      return new ThreeVector3(
        local.east * 8.4,
        local.up * 8.4,
        -local.north * 8.4,
      );
    });
  }, [snapshot.site.latitudeDegrees, snapshot.solarDeclinationDegrees]);
  const horizonRing = useMemo(
    () =>
      Array.from({ length: 65 }, (_, index) => {
        const angle = (index / 64) * Math.PI * 2;
        return new ThreeVector3(
          Math.cos(angle) * 8.4,
          0,
          Math.sin(angle) * 8.4,
        );
      }),
    [],
  );
  const altitudeArc = useMemo(() => {
    if (!snapshot.sunAboveHorizon) {
      return [];
    }
    const end = new ThreeVector3(...sunPosition);
    const start = new ThreeVector3(end.x, 0, end.z);
    if (start.lengthSq() < 1e-6) {
      return [new ThreeVector3(0, 0, -8.4), end];
    }
    start.setLength(8.4);
    return Array.from({ length: 17 }, (_, index) => {
      const t = index / 16;
      return new ThreeVector3().lerpVectors(start, end, t).setLength(8.4);
    });
  }, [snapshot.sunAboveHorizon, sunPosition]);

  const facing = useMemo<OrbitFacing>(
    () => ({
      // Camera sits opposite the Sun's horizontal direction to look at it.
      azimuthRadians: Math.atan2(-snapshot.localSun[0], -snapshot.localSun[2]),
      polarRadians:
        ((88 + Math.max(snapshot.altitudeDegrees, 0) * 0.15) * Math.PI) / 180,
      cue: viewCue,
    }),
    [snapshot.altitudeDegrees, snapshot.localSun, viewCue],
  );

  return (
    <>
      <color attach="background" args={[skyColor(snapshot.altitudeDegrees)]} />
      <hemisphereLight
        args={[
          snapshot.sunAboveHorizon ? "#d7ecff" : "#1a2740",
          "#1a140c",
          snapshot.sunAboveHorizon ? 0.55 : 0.12,
        ]}
      />
      <ambientLight
        color={snapshot.sunAboveHorizon ? "#8fb4d8" : "#1b2433"}
        intensity={snapshot.sunAboveHorizon ? 0.28 : 0.08}
      />
      {snapshot.sunAboveHorizon ? (
        <directionalLight
          color="#fff4d6"
          intensity={2.4}
          position={sunPosition}
        />
      ) : null}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[8.4, 64]} />
        <meshStandardMaterial
          color="#2a4434"
          emissive="#111c15"
          roughness={0.92}
        />
      </mesh>
      <mesh>
        <sphereGeometry args={[16, 32, 20, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshBasicMaterial
          color={skyColor(snapshot.altitudeDegrees)}
          side={BackSide}
        />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[8.25, 8.55, 96]} />
        <meshBasicMaterial
          color="#9cc3f0"
          opacity={0.55}
          side={DoubleSide}
          transparent
        />
      </mesh>
      <LinePath color="#70a9c7" points={horizonRing} />
      <LinePath color="#f5b84b" points={pathPoints} />
      {altitudeArc.length > 0 ? (
        <LinePath color="#dce8f8" points={altitudeArc} />
      ) : null}
      {snapshot.sunAboveHorizon ? (
        <Sun
          classroomMode={classroomMode}
          position={sunPosition}
          radius={0.72}
        />
      ) : null}
      <LabelSprite color="#dce8f8" position={[0, 0.35, -8.6]} text="North" />
      <LabelSprite color="#94a3bb" position={[8.6, 0.35, 0]} text="East" />
      <LabelSprite color="#94a3bb" position={[-8.6, 0.35, 0]} text="West" />
      <LabelSprite color="#94a3bb" position={[0, 0.35, 8.6]} text="South" />
      <LabelSprite
        color="#ffd166"
        position={[0, 8.2, 0]}
        text={snapshot.site.name}
      />
      <SimpleOrbitRig
        facing={facing}
        maxDistance={9}
        maxPolarAngleDegrees={100}
        minDistance={2.2}
        target={OBSERVER_TARGET}
      />
    </>
  );
}
